import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Typography, Box, Paper, Grid, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Alert, Snackbar, Tabs, Tab, FormControl, InputLabel, Select,
  MenuItem, Card, CardContent, Divider, Pagination, CircularProgress, Tooltip,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Refresh as RefreshIcon, QrCode2 as QrCodeIcon, Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon, Assessment as AssessmentIcon, Build as BuildIcon,
  SelectAll as SelectAllIcon, Clear as ClearIcon, Help as HelpIcon,
  Done as TickIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import {
  F_TASKS_HK, CreateTaskModel, EditData, MultiEditModel, Stage2QueryModel,
  Stage2QueryResponse, Stage2EditResponse, Stage2Statistics, StageOption,
  ValidationResponse
} from '../../types/stage2';
import PageLayout from '../../components/Layout/PageLayout';
import BackButton from '../../components/BackButton';
import { matchWebSubroutinesName } from './types';
import { useTranslation } from '../../hooks/useTranslation';
import { useTableSort } from '../../hooks/useTableSort';
import SortableTableHead from '../../components/Table/SortableTableHead';
import { useRole } from '../../hooks/useRole';

// ColorAlert 計算函數
const calculateColorAlert = (task: F_TASKS_HK): string => {
  if (!task.CREATED_DATE || !task.DEADLINE || task.DEADLINE_ID4 === "30") {
    return "#ffffff"; // 白色 - 默認
  }

  const createdDate = new Date(task.CREATED_DATE);
  const deadline = parseInt(task.DEADLINE);

  if (isNaN(deadline)) {
    return "#ffffff"; // 白色 - 默認
  }

  const targetDate = new Date(createdDate);
  targetDate.setDate(targetDate.getDate() + deadline);

  const now = new Date();
  const daysRemaining = Math.ceil((targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  if (daysRemaining <= 0) {
    return "#000000"; // 黑色 - 已過期
  } else if (daysRemaining <= 3) {
    return "#ff0000"; // 紅色 - 緊急
  } else if (daysRemaining <= 7) {
    return "#ffff00"; // 黃色 - 警告
  } else if (daysRemaining <= 21) {
    return "#e7e5e5"; // 淺灰色 - 注意
  } else {
    return "#ffffff"; // 白色 - 正常
  }
};

const RFIDManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { isAdminOrManager, isAdmin, isManager, isReader, isStaff } = useRole();

  // 狀態管理
  const [tasks, setTasks] = useState<F_TASKS_HK[]>([]);
  const [loading, setLoading] = useState(false);

  // 表格排序功能
  const getValue = (item: F_TASKS_HK, key: string) => {
    switch (key) {
      case 'rfid':
        return item.CBT_RFD_SN;
      case 'cubeMark':
        return item.CUBEMARK;
      case 'mouldNo':
        return item.MOULD_NO;
      case 'ageDay':
        return item.DEADLINE;
      case 'cubeType':
        return item.CUBE_TYPE;
      case 'mixID':
        return item.MIX_ID;
      case 'grade':
        return item.GRADE;
      case 'siteName':
        return item.TASK_FROM;
      case 'uploader':
        return item.UPLOADED_BY; // Using UPLOADED_BY as uploader
      case 'eventID':
        return item.WEB_SUBROUTINES_NAME; // Using CBT_RFD_UID as event ID
      case 'createdDate':
        return new Date(item.CREATED_DATE || '');
      case 'colorAlert':
        return calculateColorAlert(item);
      default:
        return '';
    }
  };

  const { sortConfig, handleSort, sortedData } = useTableSort(tasks, getValue);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openSingleEditDialog, setOpenSingleEditDialog] = useState(false);
  const [editingData, setEditingData] = useState<EditData[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });

  // 選擇狀態管理
  const [selectedTasks, setSelectedTasks] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // 分頁狀態
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // 查詢條件
  const [queryParams, setQueryParams] = useState<Stage2QueryModel>({
    page: 1,
    pageSize: 20,
    stage: "EVENT_ID_2",
    startTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7天前
    endTime: new Date().toISOString().split('T')[0] // 今天
  });

  // 階段選項
  const [stages, setStages] = useState<StageOption[]>([]);

  // 統計信息
  // const [statistics, setStatistics] = useState<Stage2Statistics | null>(null);

  // 顏色警報說明對話框
  const [openColorAlertDialog, setOpenColorAlertDialog] = useState(false);

  // 表單數據
  const [createFormData, setCreateFormData] = useState<CreateTaskModel>({
    CBT_RFD_SN: '',
    CUBEMARK: '',
    TASK_FROM: '',
    TASK_FROM_ACC: '',
    MIX_ID: '',
    MOULD_NO: '',
    GRADE: '',
    CREATED_DATE: '',
    Age_day: ''
  });

  // 批量編輯表單數據
  const [batchEditData, setBatchEditData] = useState({
    CUBEMARK: '',
    MOULD_NO: '',
    CUBE_TYPE: '',
    MIX_ID: '',
    GRADE: '',
    DEADLINE: ''
  });

  // 單個編輯表單數據
  const [singleEditData, setSingleEditData] = useState<F_TASKS_HK | null>(null);

  // 使用狀態追蹤是否已經初始化
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
  const hasInitialSearch = useRef(false);

  // 從 URL 參數初始化查詢條件（只在首次加載時執行）
  useEffect(() => {
    if (urlParamsProcessed) return;

    const urlParams: Partial<Stage2QueryModel> = {};

    // 讀取所有可能的 URL 參數
    const rfid = searchParams.get('RFID');
    const cubeMark = searchParams.get('CubeMark');
    const mixId = searchParams.get('Mix_ID');
    const staff = searchParams.get('Staff');
    const siteName = searchParams.get('SiteName');
    const stage = searchParams.get('stage');
    const startTime = searchParams.get('startTime');
    const endTime = searchParams.get('endTime');

    if (rfid) urlParams.RFID = rfid;
    if (cubeMark) urlParams.CubeMark = cubeMark;
    if (mixId) urlParams.Mix_ID = mixId;
    if (staff) urlParams.Staff = staff;
    if (siteName) urlParams.SiteName = siteName;
    if (stage) urlParams.stage = stage;
    if (startTime) urlParams.startTime = startTime;
    if (endTime) urlParams.endTime = endTime;

    // 如果有 URL 參數，更新查詢條件
    if (Object.keys(urlParams).length > 0) {
      setQueryParams(prev => ({
        ...prev,
        ...urlParams,
        stage: urlParams.stage || prev.stage || "EVENT_ID_2",
        startTime: urlParams.startTime || prev.startTime,
        endTime: urlParams.endTime || prev.endTime
      }));
      setCurrentPage(1);
    }

    setUrlParamsProcessed(true);
  }, [searchParams, urlParamsProcessed]);

  useEffect(() => {
    fetchStageOptions();
  }, []);

  // 初始加載時執行搜索（在 URL 參數處理完成後，只執行一次）
  useEffect(() => {
    if (urlParamsProcessed && !hasInitialSearch.current) {
      hasInitialSearch.current = true;
      fetchStage2Data();
    }
  }, [urlParamsProcessed]);

  // 當分頁改變時執行搜索
  useEffect(() => {
    if (urlParamsProcessed && hasInitialSearch.current) {
      fetchStage2Data();
    }
  }, [currentPage]);

  // 處理全選/取消全選
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const allTaskIds = new Set(tasks.map(task => task.TASK_ID));
      setSelectedTasks(allTaskIds);
    } else {
      setSelectedTasks(new Set());
    }
  };

  // 處理單個任務選擇
  const handleTaskSelect = (taskId: number, checked: boolean) => {
    const newSelected = new Set(selectedTasks);
    if (checked) {
      newSelected.add(taskId);
    } else {
      newSelected.delete(taskId);
    }
    setSelectedTasks(newSelected);

    // 更新全選狀態
    setSelectAll(newSelected.size === tasks.length && tasks.length > 0);
  };

  // 清除所有選擇
  const handleClearSelection = () => {
    setSelectedTasks(new Set());
    setSelectAll(false);
  };

  // 獲取階段選項
  const fetchStageOptions = async () => {
    try {
      const response = await apiService.getStage2Stages();
      setStages(response);
    } catch (error) {
      console.error('獲取階段選項失敗:', error);
    }
  };

  // 獲取 Stage2 數據
  const fetchStage2Data = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        ...queryParams,
        page: currentPage,
        pageSize: pageSize
      };

      const response: Stage2QueryResponse = await apiService.getStage2Data(params);

      setTasks(response.items);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      setCurrentPage(response.page);
    } catch (error) {
      console.error('獲取 Stage2 數據失敗:', error);
      setError('獲取 Stage2 數據失敗，請檢查網絡連接或聯繫管理員');
      showSnackbar('獲取 Stage2 數據失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 處理查詢
  const handleQuery = () => {
    // 驗證時間範圍
    if (queryParams.startTime && queryParams.endTime) {
      const startDate = new Date(queryParams.startTime);
      const endDate = new Date(queryParams.endTime);

      if (endDate < startDate) {
        showSnackbar('結束時間不能早於開始時間，請重新選擇', 'warning');
        return;
      }
    }

    setCurrentPage(1);
    fetchStage2Data();
  };

  // 處理 Enter 鍵觸發搜索
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !loading) {
      event.preventDefault();
      handleQuery();
    }
  };

  // 重置篩選條件
  const handleResetFilters = () => {
    const defaultParams: Stage2QueryModel = {
      page: 1,
      pageSize: 20,
      stage: "EVENT_ID_2",
      RFID: '',
      CubeMark: '',
      Mix_ID: '',
      Staff: '',
      SiteName: '',
      startTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7天前
      endTime: new Date().toISOString().split('T')[0] // 今天
    };
    setQueryParams(defaultParams);
    setCurrentPage(1);
    // 重置後自動執行查詢
    fetchStage2Data();
  };

  // 處理分頁
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  // 創建新任務
  const handleCreateTask = async () => {
    try {
      if (createFormData.CBT_RFD_SN.length !== 11) {
        showSnackbar(t('pages.rfidManagement.messages.rfidRequired'), 'error');
        return;
      }
      if (createFormData.CUBEMARK == "") {
        showSnackbar(t('pages.rfidManagement.messages.cubeMarkRequired'), 'error');
        return;
      }
      if (!createFormData.CREATED_DATE) {
        showSnackbar(t('pages.rfidManagement.messages.createdDateRequired'), 'error');
        return;
      }
      if (createFormData.TASK_FROM == "") {
        showSnackbar(t('pages.rfidManagement.messages.siteNameRequired'), 'error');
        return;
      }
      if (createFormData.Age_day == "") {
        showSnackbar(t('pages.rfidManagement.messages.ageDayRequired'), 'error');
        return;
      }

      await apiService.createStage2Task(createFormData);
      showSnackbar(t('pages.rfidManagement.messages.createSuccess'), 'success');
      setOpenCreateDialog(false);
      resetCreateForm();
      fetchStage2Data();
    } catch (error: any) {
      console.error('創建任務失敗:', error);
      if (error.response.data.message) {
        showSnackbar(error.response.data.message, 'error');
      } else {
        showSnackbar(t('pages.rfidManagement.messages.createFailed'), 'error');
      }
    }
  };

  // 重置創建表單
  const resetCreateForm = () => {
    setCreateFormData({
      CBT_RFD_SN: '',
      CUBEMARK: '',
      TASK_FROM: '',
      MIX_ID: '',
      MOULD_NO: '',
      GRADE: '',
      Age_day: ''
    });
  };

  // 獲取編輯數據
  const handleGetEditData = async () => {
    try {
      if (!queryParams.RFID && !queryParams.CubeMark) {
        showSnackbar('請輸入 RFID 或 Cube Mark', 'warning');
        return;
      }

      const rfidSNs = queryParams.RFID ? [queryParams.RFID] : [];
      const response: Stage2EditResponse = await apiService.getStage2EditData(rfidSNs);

      if (response.Datas && response.Datas.length > 0) {
        setEditingData(response.Datas);
        setOpenEditDialog(true);
      } else {
        showSnackbar('沒有找到可編輯的數據', 'info');
      }
    } catch (error) {
      console.error('獲取編輯數據失敗:', error);
      showSnackbar('獲取編輯數據失敗', 'error');
    }
  };

  // 批量編輯
  const handleMultiEdit = async () => {
    try {
      const multiEditData: MultiEditModel = {
        Datas: editingData.map(d => ({
          ...d,
          WEB_SUBROUTINES_NAME: d.WEB_SUBROUTINES_NAME || '',
          STATUS: d.STATUS || '',
        }))
      };

      // await apiService.multiEditStage2Tasks(multiEditData);
      showSnackbar('批量編輯成功', 'success');
      setOpenEditDialog(false);
      fetchStage2Data();
    } catch (error) {
      console.error('批量編輯失敗:', error);
      showSnackbar('批量編輯失敗', 'error');
    }
  };

  // 批量編輯
  const handleBatchEdit = async () => {
    if (selectedTasks.size === 0) {
      showSnackbar('請選擇要編輯的任務', 'warning');
      return;
    }

    try {
      const selectedTaskIds = Array.from(selectedTasks);
      const tasksToEdit = tasks.filter(task => selectedTaskIds.includes(task.TASK_ID));
      //await apiService.multiEditStage2Tasks(multiEditData);
      showSnackbar(`成功批量編輯 ${selectedTasks.size} 個任務`, 'success');

      // 清除選擇和表單
      handleClearSelection();
      setBatchEditData({
        CUBEMARK: '',
        MOULD_NO: '',
        CUBE_TYPE: '',
        MIX_ID: '',
        GRADE: '',
        DEADLINE: ''
      });

      // 刷新數據
      fetchStage2Data();
    } catch (error) {
      console.error('批量編輯失敗:', error);
      showSnackbar('批量編輯失敗', 'error');
    }
  };



  // 導出數據
  const handleExport = async (format: 'excel' | 'csv') => {
    try {
      let data;
      if (format === 'excel') {
        data = await apiService.exportStage2ToExcel(queryParams);
      } else {
        data = await apiService.exportStage2ToCSV(queryParams);
      }

      // 創建下載鏈接
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Stage2_Data_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar(`${format.toUpperCase()} 導出成功`, 'success');
    } catch (error) {
      console.error('導出失敗:', error);
      showSnackbar('導出失敗', 'error');
    }
  };

  // 驗證 RFID
  const handleValidateRFID = async (rfid: string) => {
    try {
      const response: ValidationResponse = await apiService.validateStage2RFID(rfid);
      if (response.isValid) {
        showSnackbar('RFID 驗證通過', 'success');
      } else {
        showSnackbar(response.message, 'warning');
      }
    } catch (error) {
      console.error('RFID 驗證失敗:', error);
      showSnackbar('RFID 驗證失敗', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  // 處理單個任務編輯
  const handleEditTask = (task: F_TASKS_HK) => {
    setSingleEditData(task);
    setOpenSingleEditDialog(true);
  };

  // 保存單個編輯
  const handleSaveSingleEdit = async () => {
    if (!singleEditData) return;

    try {

      await apiService.updateStage2Task(singleEditData.TASK_ID, singleEditData);

      showSnackbar(t('pages.rfidManagement.messages.updateSuccess'), 'success');
      setOpenSingleEditDialog(false);
      setSingleEditData(null);
      fetchStage2Data();
    } catch (error) {
      console.error('更新任務失敗:', error);
      showSnackbar(t('pages.rfidManagement.messages.updateFailed'), 'error');
    }
  };

  // 處理批量編輯導航
  const handleBatchEditNavigation = () => {
    if (selectedTasks.size === 0) {
      showSnackbar('請選擇要編輯的任務', 'warning');
      return;
    }

    const selectedTaskIds = Array.from(selectedTasks);
    const tasksToEdit = tasks.filter(task => selectedTaskIds.includes(task.TASK_ID));

    navigate('/rfid/multi-edit', { state: { selectedTasks: tasksToEdit } });
  };

  // 定義 filteredTasks
  const filteredTasks = tasks;

  if (loading && tasks.length === 0) {
    return (
      <PageLayout
        title={t('pages.rfidManagement.title')}
        showBackButton={true}
        breadcrumbs={[
          { label: t('pages.rfidManagement.title') }
        ]}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {t('pages.rfidManagement.messages.loading')}
          </Typography>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('pages.rfidManagement.title')}
      showBackButton={true}
      breadcrumbs={[
        { label: t('pages.rfidManagement.title') }
      ]}
    >
      <Container maxWidth="xl" sx={{ py: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 查詢條件 */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label={t('pages.rfidManagement.fields.rfid')}
                value={queryParams.RFID}
                onChange={(e) => setQueryParams({ ...queryParams, RFID: e.target.value })}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label={t('pages.rfidManagement.fields.cubeMark')}
                value={queryParams.CubeMark}
                onChange={(e) => setQueryParams({ ...queryParams, CubeMark: e.target.value })}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label={t('pages.rfidManagement.fields.mixID')}
                value={queryParams.Mix_ID}
                onChange={(e) => setQueryParams({ ...queryParams, Mix_ID: e.target.value })}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            {/* Staff 過濾欄位：只有 Admin、Manager 或 Reader 可以看到（遵循舊項目邏輯） */}
            {(isAdmin || isManager || isReader) && (
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('pages.rfidManagement.fields.uploader')}
                  value={queryParams.Staff}
                  onChange={(e) => setQueryParams({ ...queryParams, Staff: e.target.value })}
                  onKeyDown={handleKeyDown}
                  disabled={!isAdmin && !isManager} // 只有 Admin 和 Manager 可以使用
                />
              </Grid>
            )}

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label={t('pages.rfidManagement.fields.siteName')}
                value={queryParams.SiteName}
                onChange={(e) => setQueryParams({ ...queryParams, SiteName: e.target.value })}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Event ID</InputLabel>
                <Select
                  value={queryParams.stage}
                  onChange={(e) => setQueryParams({ ...queryParams, stage: e.target.value })}
                  label="Event ID"
                >
                  <MenuItem value="">全部Event ID</MenuItem>
                  {stages.map(stage => (
                    <MenuItem key={stage.value} value={stage.value}>
                      {stage.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('common.startTime')}
                value={queryParams.startTime}
                onChange={(e) => setQueryParams({ ...queryParams, startTime: e.target.value })}
                onKeyDown={handleKeyDown}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  inputProps: {
                    min: new Date(Date.now() - 183 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('common.endTime')}
                value={queryParams.endTime}
                onChange={(e) => setQueryParams({ ...queryParams, endTime: e.target.value })}
                onKeyDown={handleKeyDown}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleQuery}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                {loading ? t('pages.rfidManagement.messages.loading') : t('common.query')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleResetFilters}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                {t('common.reset')}
              </Button>
               <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setOpenCreateDialog(true)
                  resetCreateForm();
                }}
                disabled={!isAdminOrManager}
                sx={{ mr: 1 }}
              >
                {t('pages.rfidManagement.actions.createRFID')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleExport('excel')}
                sx={{ mr: 1 }}
              >
                {t('pages.rfidManagement.actions.exportExcel')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<HelpIcon />}
                onClick={() => setOpenColorAlertDialog(true)}
              >
                {t('pages.rfidManagement.actions.colorAlertHelp')}
              </Button>
            </Grid>
          </Grid>

          {/* 總計數顯示 */}
          <Box sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {t('common.queryResult')} <strong style={{ color: '#1976d2' }}>{totalCount}</strong> {t('common.records')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography variant="body2" color="text.secondary">
                  {t('common.pageNumber').replace('{page}', currentPage.toString())}，{t('common.totalPage').replace('{totalPages}', totalPages.toString())}
                  {totalCount > 0 && (
                    t('common.pageSize').replace('{pageSize}', pageSize.toString())
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* 批量操作工具欄 */}
        {selectedTasks.size > 0 && (
          <Paper sx={{ p: 2, mb: 3, backgroundColor: 'primary.light', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1">
                {t('common.selectedTasks').replace('{count}', selectedTasks.size.toString())}
              </Typography>
               <Button
                variant="contained"
                color="secondary"
                startIcon={<BuildIcon />}
                onClick={handleBatchEditNavigation}
                size="small"
                disabled={!isAdminOrManager}
              >
                {t('pages.rfidManagement.batchOperations.batchEdit')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<TickIcon />}
                onClick={() => handleSelectAll(true)}
                size="small"
                sx={{ color: 'white', borderColor: 'white' }}
              >
                {t('pages.rfidManagement.batchOperations.selectAll')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearSelection}
                size="small"
                sx={{ color: 'white', borderColor: 'white' }}
              >
                {t('pages.rfidManagement.batchOperations.clearSelection')}
              </Button>
            </Box>
          </Paper>
        )}

        {/* 數據表格 */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
              {t('common.loading')}
            </Typography>
          </Box>
        )}

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 600,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}
        >
          <Table stickyHeader>
            <SortableTableHead
              columns={[
                { key: 'checkbox', label: '', sortable: false, width: '50px' },
                { key: 'rfid', label: t('pages.rfidManagement.fields.rfid') },
                { key: 'cubeMark', label: t('pages.rfidManagement.fields.cubeMark') },
                { key: 'mouldNo', label: t('pages.rfidManagement.fields.mouldNo') },
                { key: 'ageDay', label: t('pages.rfidManagement.fields.ageDay') },
                { key: 'cubeType', label: t('pages.rfidManagement.fields.cubeType') },
                { key: 'mixID', label: t('pages.rfidManagement.fields.mixID') },
                { key: 'grade', label: t('pages.rfidManagement.fields.grade') },
                { key: 'siteName', label: t('pages.rfidManagement.fields.siteName') },
                { key: 'uploader', label: t('pages.rfidManagement.fields.uploader') },
                { key: 'eventID', label: t('pages.rfidManagement.fields.eventID') },
                { key: 'createdDate', label: t('pages.rfidManagement.fields.createdDate') },
                { key: 'colorAlert', label: t('pages.rfidManagement.fields.colorAlert') },
                { key: 'action', label: t('common.action'), sortable: false }
              ]}
              sortConfig={sortConfig}
              onSort={handleSort}
              stickyHeader={true}
            />
            <TableBody>
              {sortedData.map((task, index) => (
                <TableRow
                  key={task.TASK_ID}
                  sx={{
                    backgroundColor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                    transition: 'all 0.2s ease-in-out',
                    '& .MuiTableCell-root': {
                      borderBottom: '1px solid',
                      borderBottomColor: 'divider',
                      padding: '12px 16px',
                    }
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedTasks.has(task.TASK_ID)}
                      onChange={(e) => handleTaskSelect(task.TASK_ID, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 'medium',
                    color: 'info'
                  }}>
                    {task.CBT_RFD_SN}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'medium' }}>
                    {task.CUBEMARK}
                  </TableCell>
                  <TableCell>{task.MOULD_NO}</TableCell>
                  <TableCell>
                    <Chip
                      label={task.DEADLINE}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  </TableCell>
                  <TableCell>{task.CUBE_TYPE}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {task.MIX_ID}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.GRADE || '-'}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>{task.TASK_FROM}</TableCell>

                  <TableCell>{task.UPLOADED_BY}</TableCell>
                  <TableCell>
                    <Chip
                      label={matchWebSubroutinesName(task.WEB_SUBROUTINES_NAME || '')}
                      color={
                        task.WEB_SUBROUTINES_NAME?.includes('EVENT_ID_2') ? 'primary' :
                          task.WEB_SUBROUTINES_NAME?.includes('EVENT_ID_3') ? 'secondary' : 'default'
                      }
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 'medium' }}
                    />
                  </TableCell>
                  <TableCell>
                    {task.CREATED_DATE ? new Date(task.CREATED_DATE).toLocaleDateString('en-GB') : '-'}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: calculateColorAlert(task),
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        display: 'inline-block',
                        cursor: 'default'
                      }}
                      onClick={() => setOpenColorAlertDialog(true)}
                      title={`Created: ${task.CREATED_DATE || 'N/A'}, Deadline: ${task.DEADLINE || 'N/A'} days`}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>

                      <Tooltip title={t('common.edit')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditTask(task)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'white',
                            }
                          }}
                          disabled={isReader} // Reader 不能編輯，其他角色（包括 Staff）可以編輯（遵循舊項目邏輯）
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 分頁 */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* 創建任務對話框 */}
        <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>{t('pages.rfidManagement.createDialog.title')}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('pages.rfidManagement.createDialog.fields.rfid')}
                  value={createFormData.CBT_RFD_SN}
                  onChange={(e) => setCreateFormData({ ...createFormData, CBT_RFD_SN: e.target.value })}
                  inputProps={{ maxLength: 11 }}
                  helperText={`${createFormData.CBT_RFD_SN.length}/11`}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('pages.rfidManagement.createDialog.fields.cubeMark')}
                  value={createFormData.CUBEMARK}
                  onChange={(e) => setCreateFormData({ ...createFormData, CUBEMARK: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('pages.rfidManagement.createDialog.fields.grade')}
                  value={createFormData.GRADE}
                  onChange={(e) => setCreateFormData({ ...createFormData, GRADE: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label={t('pages.rfidManagement.createDialog.fields.createdDate')}
                  value={createFormData.CREATED_DATE}
                  onChange={(e) => setCreateFormData({ ...createFormData, CREATED_DATE: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('pages.rfidManagement.createDialog.fields.siteName')}
                  value={createFormData.TASK_FROM}
                  onChange={(e) => setCreateFormData({ ...createFormData, TASK_FROM: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('pages.rfidManagement.createDialog.fields.ageDay')}
                  value={createFormData.Age_day}
                  onChange={(e) => setCreateFormData({ ...createFormData, Age_day: e.target.value.toString() })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('pages.rfidManagement.createDialog.fields.mixID')}
                  value={createFormData.MIX_ID}
                  onChange={(e) => setCreateFormData({ ...createFormData, MIX_ID: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('pages.rfidManagement.createDialog.fields.mouldNo')}
                  value={createFormData.MOULD_NO}
                  onChange={(e) => setCreateFormData({ ...createFormData, MOULD_NO: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreateDialog(false)}>{t('pages.rfidManagement.actions.cancel')}</Button>
            <Button onClick={handleCreateTask} variant="contained">
              {t('common.submit')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 單個編輯對話框 */}
        <Dialog open={openSingleEditDialog} onClose={() => setOpenSingleEditDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>{t('pages.rfidManagement.editDialog.title')}</DialogTitle>
          <DialogContent>
            {singleEditData && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('pages.rfidManagement.editDialog.fields.rfid')}
                    value={singleEditData.CBT_RFD_SN}
                    onChange={(e) => setSingleEditData({ ...singleEditData, CBT_RFD_SN: e.target.value })}
                    disabled
                    helperText={t('pages.rfidManagement.editDialog.fields.rfidDisabled')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('pages.rfidManagement.editDialog.fields.cubeMark')}
                    value={singleEditData.CUBEMARK}
                    onChange={(e) => setSingleEditData({ ...singleEditData, CUBEMARK: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('pages.rfidManagement.editDialog.fields.mouldNo')}
                    value={singleEditData.MOULD_NO}
                    onChange={(e) => setSingleEditData({ ...singleEditData, MOULD_NO: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('pages.rfidManagement.editDialog.fields.ageDay')}
                    value={singleEditData.DEADLINE}
                    onChange={(e) => setSingleEditData({ ...singleEditData, DEADLINE: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('pages.rfidManagement.editDialog.fields.cubeType')}
                    value={singleEditData.CUBE_TYPE}
                    onChange={(e) => setSingleEditData({ ...singleEditData, CUBE_TYPE: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('pages.rfidManagement.editDialog.fields.mixID')}
                    value={singleEditData.MIX_ID}
                    onChange={(e) => setSingleEditData({ ...singleEditData, MIX_ID: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('pages.rfidManagement.editDialog.fields.grade')}
                    value={singleEditData.GRADE}
                    onChange={(e) => setSingleEditData({ ...singleEditData, GRADE: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('pages.rfidManagement.editDialog.fields.siteName')}
                    value={singleEditData.TASK_FROM}
                    onChange={(e) => setSingleEditData({ ...singleEditData, TASK_FROM: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('pages.rfidManagement.editDialog.fields.uploader')}
                    value={singleEditData.UPLOADED_BY}
                    onChange={(e) => setSingleEditData({ ...singleEditData, UPLOADED_BY: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('pages.rfidManagement.editDialog.fields.eventID')}
                    value={singleEditData.WEB_SUBROUTINES_NAME}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    disabled
                    label={t('pages.rfidManagement.editDialog.fields.createdDate')}
                    value={singleEditData.CREATED_DATE ? new Date(singleEditData.CREATED_DATE).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setSingleEditData({ ...singleEditData, CREATED_DATE: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSingleEditDialog(false)}>{t('pages.rfidManagement.actions.cancel')}</Button>
            <Button onClick={handleSaveSingleEdit} variant="contained">
              {t('pages.rfidManagement.actions.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 提示信息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* 顏色警報說明對話框 */}
        <Dialog
          open={openColorAlertDialog}
          onClose={() => setOpenColorAlertDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {t('pages.rfidManagement.colorAlert.title')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('pages.rfidManagement.colorAlert.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('pages.rfidManagement.colorAlert.description')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: '#000000',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}
                />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t('pages.rfidManagement.colorAlert.black')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('pages.rfidManagement.colorAlert.blackDesc')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: '#ff0000',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}
                />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t('pages.rfidManagement.colorAlert.red')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('pages.rfidManagement.colorAlert.redDesc')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: '#ffff00',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}
                />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t('pages.rfidManagement.colorAlert.yellow')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('pages.rfidManagement.colorAlert.yellowDesc')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: '#e7e5e5',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}
                />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t('pages.rfidManagement.colorAlert.lightGray')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('pages.rfidManagement.colorAlert.lightGrayDesc')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: '#ffffff',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}
                />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t('pages.rfidManagement.colorAlert.white')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('pages.rfidManagement.colorAlert.whiteDesc')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('pages.rfidManagement.colorAlert.formula')}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>{t('pages.rfidManagement.colorAlert.note')}</strong>
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenColorAlertDialog(false)}>
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PageLayout>
  );
};

export default RFIDManagement;
